import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ListsGate } from "@/components/ListsGate";
import { AdminRoute } from "@/components/AdminRoute";
import { TeacherRoute } from "@/components/TeacherRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { primaryRouteLoaders, lazyPage } from '@/lib/route-preload';

// Lazy-load non-critical UI overlays to reduce TTI
const FeedbackButton = lazy(() => import("@/components/FeedbackButton").then(m => ({ default: m.FeedbackButton })));
const CookieConsent = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));
const BottomNav = lazy(() => import("@/components/BottomNav").then(m => ({ default: m.BottomNav })));
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { usePWAUpdate } from "@/hooks/usePWAUpdate";
import { BottomNavVisibilityProvider } from "@/hooks/useBottomNavVisibility";
import { HdAudioPreferenceProvider } from "@/hooks/useHdAudioPreference";
import { GlobalErrorBoundary, RouteErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/components/ThemeProvider";
import { useStatusBarSync } from "@/hooks/useStatusBarSync";
import { useDeepLinks } from "@/hooks/useDeepLinks";
import { useForceUpdate } from "@/hooks/useForceUpdate";
import { ForceUpdateModal } from "@/components/ForceUpdateModal";

// Eagerly load Landing page for fast initial render
import Landing from "./pages/Landing";

// Lazy load all other pages to reduce initial bundle size
const Compare = lazyPage(() => import("./pages/Compare"));
const Pricing = lazyPage(() => import("./pages/Pricing"));
const Auth = lazyPage(() => import("./pages/Auth"));
const NativeOnboardingPage = lazyPage(() => import("./pages/NativeOnboardingPage"));
const ForgotPassword = lazyPage(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const Translate = lazy(primaryRouteLoaders['/translate']);
const Vocabulary = lazy(primaryRouteLoaders['/vocabulary']);
const Account = lazyPage(() => import("./pages/Account"));
const Stats = lazyPage(() => import("./pages/Stats"));
const Admin = lazyPage(() => import("./pages/Admin"));
const AdminUserDetail = lazyPage(() => import("./pages/AdminUserDetail"));
const AdminTeachers = lazyPage(() => import("./pages/admin/Teachers"));
const AdminTeacherDetail = lazyPage(() => import("./pages/admin/TeacherDetail"));
const Teach = lazyPage(() => import("./pages/Teach"));
const Conjugation = lazy(primaryRouteLoaders['/conjugation']);
const PrivacyPolicy = lazyPage(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyPage(() => import("./pages/TermsOfService"));

const Download = lazyPage(() => import("./pages/Download"));
const Features = lazyPage(() => import("./pages/Features"));
const HowToUse = lazyPage(() => import("./pages/HowToUse"));
const Study = lazy(primaryRouteLoaders['/study']);
const About = lazyPage(() => import("./pages/About"));
const Lists = lazyPage(() => import("./pages/Lists"));
const ListDetail = lazyPage(() => import("./pages/ListDetail"));
const Resources = lazyPage(() => import("./pages/Resources"));
const VerbReference = lazyPage(() => import("./pages/VerbReference"));
const SituationalPhrasesList = lazyPage(() => import("./pages/SituationalPhrasesList"));
const SituationalPhraseDetail = lazyPage(() => import("./pages/SituationalPhraseDetail"));
const EmotionsReference = lazyPage(() => import("./pages/EmotionsReference"));
const AdjectivesReference = lazyPage(() => import("./pages/AdjectivesReference"));
const ConjunctionsReference = lazyPage(() => import("./pages/ConjunctionsReference"));
const SecuritySettings = lazyPage(() => import("./pages/SecuritySettings"));
const PrivacySettings = lazyPage(() => import("./pages/PrivacySettings"));
const AccountClasses = lazyPage(() => import("./pages/account/Classes"));
const ImportVocabulary = lazyPage(() => import("./pages/ImportVocabulary"));
const RestoreBackup = lazyPage(() => import("./pages/RestoreBackup"));
const TranslationHistory = lazyPage(() => import("./pages/TranslationHistory"));
const Support = lazyPage(() => import("./pages/Support"));
const Schools = lazyPage(() => import("./pages/Schools"));
const NotFound = lazyPage(() => import("./pages/NotFound"));
const SmartRedirect = lazyPage(() => import("./pages/SmartRedirect"));

const queryClient = new QueryClient();

// Use same spinner as ProtectedRoute to avoid visual flicker during transitions
import { FullPageLoader } from '@/components/ui/full-page-loader';

const PageLoader = () => <FullPageLoader />;

// Component that must be inside Router to use useNavigate
const DeepLinkHandler = () => {
  useDeepLinks();
  return null;
};

const isRedirectDomain = window.location.hostname.includes('lekatranslate');

const AppContent = () => {
  const { needRefresh, applyUpdate } = usePWAUpdate();
  const { updateRequired, updateRecommended, storeUrl } = useForceUpdate();
  useStatusBarSync();

  if (isRedirectDomain) {
    return (
      <Suspense fallback={<PageLoader />}>
        <SmartRedirect />
      </Suspense>
    );
  }
  
  return (
    <>
      {updateRequired && <ForceUpdateModal mode="required" storeUrl={storeUrl} />}
      {updateRecommended && <ForceUpdateModal mode="recommended" storeUrl={storeUrl} />}
      <Toaster />
      <BrowserRouter>
        <DeepLinkHandler />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <RouteErrorBoundary>
            <Routes>
              {/* Public/marketing routes - low risk */}
              <Route path="/" element={<Landing />} />
              <Route path="/native-onboarding" element={<NativeOnboardingPage />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              
              <Route path="/download" element={<Download />} />
              <Route path="/install" element={<Navigate to="/download" replace />} />
              <Route path="/features" element={<Features />} />
              <Route path="/how-to-use" element={<HowToUse />} />
              <Route path="/about" element={<About />} />
              <Route path="/support" element={<Support />} />
              <Route path="/schools" element={<Schools />} />
              
              {/* High-risk routes - complex state, AI features */}
              <Route path="/translate" element={<ProtectedRoute><Translate /></ProtectedRoute>} />
              <Route path="/vocabulary" element={<ProtectedRoute><Vocabulary /></ProtectedRoute>} />
              <Route path="/study" element={<ProtectedRoute><Study /></ProtectedRoute>} />
              <Route path="/conjugation" element={<ProtectedRoute><Conjugation /></ProtectedRoute>} />
              
              {/* Medium-risk routes - data-dependent */}
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/account/security" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
              <Route path="/account/privacy" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
              <Route path="/account/classes" element={<ProtectedRoute><AccountClasses /></ProtectedRoute>} />
              <Route path="/account/import" element={<ProtectedRoute><ImportVocabulary /></ProtectedRoute>} />
              <Route path="/account/privacy/restore" element={<ProtectedRoute><RestoreBackup /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute><TranslationHistory /></ProtectedRoute>} />
              <Route path="/stats" element={<ProtectedRoute><Stats /></ProtectedRoute>} />
              <Route path="/lists" element={<ProtectedRoute><ListsGate><Lists /></ListsGate></ProtectedRoute>} />
              <Route path="/lists/:listId" element={<ProtectedRoute><ListsGate><ListDetail /></ListsGate></ProtectedRoute>} />
              <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
              <Route path="/resources/verbs" element={<ProtectedRoute><VerbReference /></ProtectedRoute>} />
              <Route path="/resources/emotions" element={<ProtectedRoute><EmotionsReference /></ProtectedRoute>} />
              <Route path="/resources/adjectives" element={<ProtectedRoute><AdjectivesReference /></ProtectedRoute>} />
              <Route path="/resources/conjunctions" element={<ProtectedRoute><ConjunctionsReference /></ProtectedRoute>} />
              <Route path="/resources/phrases" element={<ProtectedRoute><SituationalPhrasesList /></ProtectedRoute>} />
              <Route path="/resources/phrases/:topicId" element={<ProtectedRoute><SituationalPhraseDetail /></ProtectedRoute>} />
              
              {/* Admin routes */}
              <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
              <Route path="/admin/user/:userId" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
              <Route path="/admin/teachers" element={<AdminRoute><AdminTeachers /></AdminRoute>} />
              <Route path="/admin/teachers/:teacherId" element={<AdminRoute><AdminTeacherDetail /></AdminRoute>} />

              {/* Teacher routes (RFC-146) */}
              <Route path="/teach" element={<TeacherRoute><Teach /></TeacherRoute>} />
              
              {/* 404 fallback */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </RouteErrorBoundary>
        </Suspense>
        <Suspense fallback={null}>
          <BottomNav />
          <FeedbackButton />
          <CookieConsent />
        </Suspense>
        <PWAUpdatePrompt updateAvailable={needRefresh} onUpdate={applyUpdate} />
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <ThemeProvider
    attribute="class"
    defaultTheme="system"
    enableSystem
    disableTransitionOnChange
  >
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <HdAudioPreferenceProvider>
            <BottomNavVisibilityProvider>
              <GlobalErrorBoundary>
                <AppContent />
              </GlobalErrorBoundary>
            </BottomNavVisibilityProvider>
          </HdAudioPreferenceProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
